import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { PICKER_SCLA_CHARACTER } from 'constants/picker';
import { RequiredMark } from 'components/ui/FieldShell';
import updateSclaAppPrompts from 'features/scla/api/updateSclaAppPrompts';
import updateSclaReturnerAppPrompts from 'features/scla/api/returner/updateSclaReturnerAppPrompts';
import {
  getSclaAppSchoolPath,
  getSclaAppTranscriptsPath,
} from 'utils/navigation';
import { isSclaReturnerAppType } from 'utils/sclaApplicationKind';
import ChipButton from '../components/steps/ChipButton';
import SclaStepCard from '../components/steps/SclaStepCard';
import styles from '../components/steps/SclaStep.module.scss';
import useAutosave, { useSectionReady } from '../hooks/useAutosave';
import useSclaSectionPath from '../hooks/useSclaSectionPath';
import { SclaApplicationOutletContext } from './SclaAppLayout';
import { countWords, PROMPT_WORD_LIMIT, promptError } from '../utils/promptFields';

type PromptKey =
  | 'obstaclePrompt'
  | 'curiosityPrompt'
  | 'returnerSkillPrompt'
  | 'returnerCuriosityPrompt'
  | 'returnerCreedPrompt';

interface IPromptCopy {
  key: PromptKey;
  label: string;
}

const REGULAR_PROMPTS: IPromptCopy[] = [
  {
    key: 'obstaclePrompt',
    label:
      'Tell us about a time you were curious about something. What was your main takeaway from that experience?',
  },
  {
    key: 'curiosityPrompt',
    label: 'Write about a time you overcame an obstacle. How did you solve that problem?',
  },
];

const RETURNER_PROMPTS: IPromptCopy[] = [
  {
    key: 'returnerSkillPrompt',
    label:
      'How have you applied your skills gained through SCLA this past year in school and/or in life?',
  },
  {
    key: 'returnerCuriosityPrompt',
    label:
      'Tell us about a time curiosity got you in trouble. What was your main takeaway from that experience?',
  },
  {
    key: 'returnerCreedPrompt',
    label: 'Talk about the leadership creed that you have applied most since joining TXT and why?',
  },
];

function reveal(id: string) {
  const node = document.getElementById(id);
  if (!node) return;
  node.scrollIntoView({ behavior: 'smooth', block: 'center' });
  if ('focus' in node) (node as HTMLElement).focus();
}

interface IPromptCardProps {
  index: number;
  prompt: IPromptCopy;
  value: string;
  error: string;
  readOnly: boolean;
  onChange: (key: PromptKey, value: string) => void;
}

function PromptCard({
  index,
  prompt,
  value,
  error,
  readOnly,
  onChange,
}: IPromptCardProps) {
  const field = useRef<HTMLTextAreaElement>(null);
  const count = countWords(value);
  const over = count > PROMPT_WORD_LIMIT;

  useEffect(() => {
    const node = field.current;
    if (!node) return;
    node.style.height = 'auto';
    node.style.height = `${node.scrollHeight}px`;
  }, [value]);

  const change = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(prompt.key, event.target.value);
  };

  return (
    <div className={styles.promptCard}>
      <p className={styles.promptQuestion}>
        {`${index}. ${prompt.label}`}
        <RequiredMark />
      </p>
      <textarea
        ref={field}
        id={prompt.key}
        rows={6}
        className={error ? `${styles.promptText} ${styles.promptTextError}` : styles.promptText}
        value={value}
        disabled={readOnly}
        aria-invalid={error ? true : undefined}
        aria-describedby={
          error ? `${prompt.key}-count ${prompt.key}-error` : `${prompt.key}-count`
        }
        onChange={change}
      />
      <p
        id={`${prompt.key}-count`}
        className={over ? styles.wordCountOver : styles.wordCount}
      >
        {`${count} / ${PROMPT_WORD_LIMIT} words`}
      </p>
      {error ? (
        <p id={`${prompt.key}-error`} className={styles.fieldError} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export default function SclaApplicationPrompts() {
  const { application, setApplication, readOnly } =
    useOutletContext<SclaApplicationOutletContext>();
  const returner = isSclaReturnerAppType(application.appType);
  const prompts = application.promptInfo;
  const [words, setWords] = useState<string[]>(prompts.wordSelection || []);
  const [obstaclePrompt, setObstaclePrompt] = useState(prompts.obstaclePrompt || '');
  const [curiosityPrompt, setCuriosityPrompt] = useState(prompts.curiosityPrompt || '');
  const [returnerSkillPrompt, setReturnerSkillPrompt] = useState(prompts.returnerSkillPrompt || '');
  const [returnerCuriosityPrompt, setReturnerCuriosityPrompt] = useState(prompts.returnerCuriosityPrompt || '');
  const [returnerCreedPrompt, setReturnerCreedPrompt] = useState(prompts.returnerCreedPrompt || '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const ready = useSectionReady(application._id, () => {
    const next = application.promptInfo;
    setWords(next.wordSelection || []);
    setObstaclePrompt(next.obstaclePrompt || '');
    setCuriosityPrompt(next.curiosityPrompt || '');
    setReturnerSkillPrompt(next.returnerSkillPrompt || '');
    setReturnerCuriosityPrompt(next.returnerCuriosityPrompt || '');
    setReturnerCreedPrompt(next.returnerCreedPrompt || '');
    setErrors({});
  });
  const sectionPath = useSclaSectionPath();
  const navigate = useNavigate();
  const promptFields = returner ? RETURNER_PROMPTS : REGULAR_PROMPTS;
  const answers: Record<PromptKey, string> = {
    obstaclePrompt,
    curiosityPrompt,
    returnerSkillPrompt,
    returnerCuriosityPrompt,
    returnerCreedPrompt,
  };

  const regularValues = { obstaclePrompt, curiosityPrompt };
  const returnerValues = {
    returnerSkillPrompt,
    returnerCuriosityPrompt,
    returnerCreedPrompt,
  };

  const save = async () => {
    if (returner) {
      const response = await updateSclaReturnerAppPrompts(application._id, {
        wordSelection: words,
        ...returnerValues,
      });
      setApplication(response);
      return true;
    }
    const response = await updateSclaAppPrompts(application._id, {
      wordSelection: words,
      obstaclePrompt,
      curiosityPrompt,
    });
    setApplication(response);
    return true;
  };

  const { flush } = useAutosave({
    serialized: JSON.stringify({ words, ...regularValues, ...returnerValues }),
    enabled: !readOnly && ready,
    save,
  });

  const toggleWord = (value: string) => {
    setWords(current => {
      if (current.includes(value)) return current.filter(item => item !== value);
      if (current.length >= 3) return current;
      return [...current, value];
    });
    setErrors(current => ({ ...current, words: '' }));
  };

  const changePrompt = useCallback((key: PromptKey, value: string) => {
    if (key === 'obstaclePrompt') setObstaclePrompt(value);
    else if (key === 'curiosityPrompt') setCuriosityPrompt(value);
    else if (key === 'returnerSkillPrompt') setReturnerSkillPrompt(value);
    else if (key === 'returnerCuriosityPrompt') setReturnerCuriosityPrompt(value);
    else setReturnerCreedPrompt(value);
    const over = countWords(value) > PROMPT_WORD_LIMIT ? promptError(value) : '';
    setErrors(current => {
      const previous = current[key] || '';
      const next = over || (previous && !value.trim() ? previous : '');
      return { ...current, [key]: next };
    });
  }, []);

  const handleContinue = async () => {
    const posted = await flush();
    const nextErrors: Record<string, string> = {};
    if (words.length !== 3) nextErrors.words = 'Choose three words.';
    promptFields.forEach(prompt => {
      const message = promptError(answers[prompt.key]);
      if (message) nextErrors[prompt.key] = message;
    });
    setErrors(nextErrors);
    const firstPrompt = promptFields.find(prompt => nextErrors[prompt.key]);
    const firstId = nextErrors.words ? 'scla-words' : firstPrompt?.key;
    if (firstId) reveal(firstId);
    const ok = posted && !firstId;
    if (ok) navigate(sectionPath(getSclaAppTranscriptsPath()));
    return ok;
  };

  return (
    <SclaStepCard
      title="Prompts"
      intro="Answer in your own words. A few honest, specific sentences beat a long essay."
      backTo={sectionPath(getSclaAppSchoolPath())}
      onContinue={handleContinue}
      continueLabel="Save and continue →"
      readOnly={readOnly}
    >
      <div className={styles.control}>
      <p className={styles.question} id="scla-words" tabIndex={-1}>
        Select three words from the list below that your best friend would use to describe you.
        <RequiredMark />
      </p>
      <p className={styles.count}>{`${words.length} of 3 chosen`}</p>
      <div className={styles.chips}>
        {PICKER_SCLA_CHARACTER.map(option => {
          const selected = words.includes(option.value);
          const locked = !selected && words.length >= 3;
          const className = [styles.chip];
          if (selected) className.push(styles.chipOn);
          if (locked) className.push(styles.chipMuted);
          return (
            <ChipButton
              key={option.value}
              pressed={selected}
              className={className.join(' ')}
              disabled={readOnly || locked}
              onClick={() => toggleWord(option.value)}
            >
              {option.label}
            </ChipButton>
          );
        })}
      </div>
      {errors.words ? <p className={styles.fieldError}>{errors.words}</p> : null}
      </div>
      {promptFields.map((prompt, index) => (
        <PromptCard
          key={prompt.key}
          index={index + 1}
          prompt={prompt}
          value={answers[prompt.key]}
          error={errors[prompt.key] || ''}
          readOnly={readOnly}
          onChange={changePrompt}
        />
      ))}
    </SclaStepCard>
  );
}
